import {Disposable, Environment} from 'relay-runtime';
import {commitMutation, graphql} from 'react-relay';
import Util from '../../forecast-app/shared/util/util';
import {
	RunNovaAgentProductionInput,
	RunNovaAgentProductionMutation,
} from './__generated__/RunNovaAgentProductionMutation.graphql';
import {MutationInput, RelayOnCompleted, RelayOnError} from '../MutationTypes';

// A production run: the agent runs against a real task for a customer, and the answer is saved to
// its own run record. Nothing is written back to the task itself — an answer is read beside the
// task, never posted into it.
//
// A run started with a `threadId` is a reply on an existing conversation, and its result is a new
// turn in that conversation. The selection below mirrors the turn shape the thread's own query
// selects, so a reply's response can be appended straight to the caller's turn list and rendered
// without a refetch — the invocation control's own state (outcome, status, failureState) is just
// a subset of the same run record.
const mutation = graphql`
	mutation RunNovaAgentProductionMutation($input: RunNovaAgentProductionInput!) {
		runNovaAgentProduction(input: $input) {
			result {
				run {
					id
					runId
					threadId
					agentId
					versionNumber
					status
					outcome
					answer
					userMessageText
					ranAt
					failureState
					grounding {
						resolved {
							token
							scope
						}
					}
				}
			}
			# Selected so the store learns this task now has a conversation the moment the run lands.
			# Relay merges it into the task node every row already reads through, so the row's control
			# changes from offering a fresh run to offering to continue this one without a refetch and
			# without waiting for the next page load.
			task {
				id
				hasNovaConversation
				# The conversation itself, in the same shape the task modal's own query selects it.
				# The modal loads through ForecastQueryRenderer's store-and-network policy, and it
				# decides where to land and how much room to reserve for the composer once, from
				# whatever the store holds at that moment. Without refreshing this list here, reopening
				# a task that ran in this session made those decisions from a snapshot taken before the
				# run existed. Selected whole rather than by id: a record written with only an id would
				# leave the fields the thread renders missing from the store.
				novaThread {
					id
					runId
					threadId
					agentId
					versionNumber
					status
					outcome
					answer
					userMessageText
					ranAt
					failureState
					grounding {
						resolved {
							token
							scope
						}
						skipped {
							token
						}
					}
				}
			}
		}
	}
`;

function commit(
	environment: Environment,
	input: MutationInput<RunNovaAgentProductionInput>,
	onSuccess?: RelayOnCompleted<RunNovaAgentProductionMutation>,
	onError?: RelayOnError
): Disposable {
	const variables = {input: {csrfToken: Util.getCsrfValue(), socketClientId: Util.getClientId(), ...input}};

	return commitMutation<RunNovaAgentProductionMutation>(environment, {
		mutation,
		variables,
		onCompleted: onSuccess,
		onError: onError,
	});
}

export default {commit};
